import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import { logger } from '@/lib/logger';
import {
  CategoryResponseSchema,
  CategoriesInGroupResponseSchema,
  CategorySummaryResponseSchema,
  CategorySplitsResponseSchema,
  type Category,
  type CategoryWithSplits,
  type CategorySummary,
  type CategorySplitWithName,
} from '@/api/schemas/category.schema';

export interface CreateCategoryParams {
  groupId: string;
  name: string;
  emoji?: string;
  description?: string;
  default_splits?: Array<{
    user_id: string;
    weight: number;
  }>;
}

export interface UpdateCategoryParams {
  categoryId: string;
  name: string;
  emoji?: string;
  description?: string;
  default_splits?: Array<{
    user_id: string;
    weight: number;
  }>;
}

export interface UpdateCategorySplitsParams {
  categoryId: string;
  splits: Array<{
    user_id: string;
    weight: number;
  }>;
}

export class CategoryService extends Context.Tag('CategoryService')<
  CategoryService,
  {
    readonly create: (params: CreateCategoryParams) => Effect.Effect<Category, any>;
    readonly update: (params: UpdateCategoryParams) => Effect.Effect<Category, any>;
    readonly delete: (categoryId: string) => Effect.Effect<void, any>;
    readonly fetchForGroup: (groupId: string) => Effect.Effect<CategoryWithSplits[], any>;
    readonly fetchSummary: (categoryId: string) => Effect.Effect<CategorySummary, any>;
    readonly fetchSplits: (
      categoryId: string
    ) => Effect.Effect<CategorySplitWithName[], any>;
    readonly updateSplits: (
      params: UpdateCategorySplitsParams
    ) => Effect.Effect<CategorySplitWithName[], any>;
  }
>() {}

export const CategoryServiceLive = Layer.effect(
  CategoryService,
  Effect.gen(function* (_) {
    const api = yield* _(ApiClient);

    return {
      create: (params) => {
        const request: any = {
          group: params.groupId,
          name: params.name,
        };

        if (params.emoji) request.emoji = params.emoji;
        // Note: Backend doesn't support description or default_splits on create yet
        // TODO: Add these when backend supports them

        return pipe(
          api.post('/categories/create', request, CategoryResponseSchema),
          Effect.map((response) => response.category),
          Effect.tap(() =>
            Effect.log(`Category created: ${params.name} ${params.emoji || ''}`)
          )
        );
      },

      update: (params) => {
        const request: any = {
          name: params.name,
        };

        if (params.emoji) request.emoji = params.emoji;
        // Note: Backend doesn't support description on update yet
        // TODO: Add when backend supports it

        // For default_splits, use the separate updateSplits endpoint
        if (params.default_splits && params.default_splits.length > 0) {
          logger.info('Note: Use updateSplits() to update category splits separately');
        }

        return pipe(
          api.put(`/categories/${params.categoryId}`, request, CategoryResponseSchema),
          Effect.map((response) => response.category),
          Effect.tap(() =>
            Effect.log(`Category updated: ${params.categoryId}`)
          )
        );
      },

      delete: (categoryId) =>
        pipe(
          api.delete(`/categories/${categoryId}`),
          Effect.tap(() =>
            Effect.log(`Category deleted: ${categoryId}`)
          )
        ),

      fetchForGroup: (groupId) =>
        pipe(
          api.get(`/groups/${groupId}/categories`, CategoriesInGroupResponseSchema),
          Effect.map((response) => response.categories),
          Effect.tap((categories) =>
            Effect.log(`Fetched ${categories.length} categories for group ${groupId}`)
          )
        ),

      fetchSummary: (categoryId) =>
        pipe(
          api.get(`/categories/${categoryId}/summary`, CategorySummaryResponseSchema),
          Effect.tap(() => Effect.log(`Fetched summary for category ${categoryId}`))
        ),

      fetchSplits: (categoryId) =>
        pipe(
          api.get(`/categories/${categoryId}/splits`, CategorySplitsResponseSchema),
          Effect.map((response) => response.splits),
          Effect.tap((splits) =>
            Effect.log(`Fetched ${splits.length} splits for category ${categoryId}`)
          )
        ),

      updateSplits: (params) => {
        const request = {
          splits: params.splits.map(split => ({
            user_id: split.user_id,
            weight: typeof split.weight === 'number' ? split.weight : Number(split.weight)
          })),
        };

        return pipe(
          api.put(
            `/categories/${params.categoryId}/splits`,
            request,
            CategorySplitsResponseSchema
          ),
          Effect.map((response) => response.splits),
          Effect.tap(() =>
            Effect.log(
              `Updated splits for category ${params.categoryId}: ${params.splits.length} splits`
            )
          )
        );
      },
    };
  })
);